import { useEffect, useRef, useState, useMemo } from "react";

/**
 * Hook para debounce de búsqueda
 * @param {string} value - Valor a debounce
 * @param {number} delay - Delay en ms (default: 300)
 * @returns {string} - Valor debouncreado
 */
export function useSearchDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook para contar elementos por key
 * @param {Object} data - Objeto con arrays {clientId: [items]}
 * @returns {Map} - Mapa con conteos {clientId: count}
 */
export function useCountMap(data) {
  return useMemo(() => {
    const map = new Map();

    Object.entries(data || {}).forEach(([key, items]) => {
      map.set(key, (items || []).length);
    });

    return map;
  }, [data]);
}

/**
 * Hook para memoizar filtrado de búsqueda
 * @param {Array} items - Items a filtrar
 * @param {string} searchQuery - Query de búsqueda
 * @param {Function} searchFn - Función que retorna si un item coincide
 * @returns {Array} - Items filtrados
 */
export function useFilteredSearch(items, searchQuery, searchFn) {
  return useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }

    const query = searchQuery.toLowerCase();
    return items.filter((item) => searchFn(item, query));
  }, [items, searchQuery, searchFn]);
}
